/**
 * Live "banking-style" grouping for number inputs.
 *
 * As the user types, digits are grouped with the locale's thousands separator
 * ("3519" → "3.519") WITHOUT moving the caret. The whole difficulty is caret
 * preservation across a reformat that inserts/removes separator chars — solved
 * once here in {@link mapCaretAfterReformat} + {@link useLiveGroupedInput} and
 * reused by every numeric input (money, decimal, integer).
 *
 * STORAGE is unaffected: these produce a display/editing string only. The caller
 * derives the canonical value on blur via `parseLocaleNumber` (money/decimal) or
 * {@link stripIntegerGrouping} (integer, kept as a string so 20-digit LONGs never
 * round-trip through a lossy JS number).
 */
import { useLayoutEffect, useRef } from "react";
import { localeSeparators } from "./currency";

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Group a run of digits into locale thousand-groups ("1234567" → "1.234.567"). */
function groupDigits(digits: string, group: string): string {
  if (!digits) return "";
  let out = "";
  const len = digits.length;
  for (let i = 0; i < len; i++) {
    if (i > 0 && (len - i) % 3 === 0) out += group;
    out += digits[i];
  }
  return out;
}

/**
 * Format a raw integer input string with live grouping. Keeps a leading "-",
 * drops every non-digit (including any prior grouping chars, which are then
 * re-inserted). "10000" → "10.000"; "-1000" → "-1.000".
 */
export function formatIntegerGroupedLive(raw: string, locale: string): string {
  // Guest-field values can arrive as raw numbers from the backend (e.g. a LONG
  // additional field stored as numeric 0) — coerce so these never throw.
  raw = String(raw ?? "");
  const { group } = localeSeparators(locale);
  const neg = raw.trim().startsWith("-");
  const digits = raw.replace(/[^\d]/g, "");
  return (neg ? "-" : "") + groupDigits(digits, group);
}

/**
 * PB-TK-1 — decide whether a "." in a comma-decimal locale (de-DE: group ".",
 * decimal ",") is the user's DECIMAL POINT or a thousands separator.
 *
 * Before this, `formatDecimalGroupedLive` stripped every group char before
 * splitting on the decimal, so a typed or pasted en-style "12.99" in a
 * DOUBLE/CURRENCY cell silently became 1299 and "3.5" became 35 — a 10-100x
 * overstatement, and the same for any en-formatted spreadsheet paste.
 *
 * The hard part is that the string ALONE cannot answer this. The live
 * formatter feeds its own output back on every keystroke, so "19.234" may be
 * a user typing a decimal OR the formatter's own grouping of 19234 with a
 * digit just inserted mid-number. Guessing from the text regresses one or the
 * other. So the previous DISPLAY is the discriminator:
 *
 *  - Incremental edit (one char changed) that ADDED a "." → the user just
 *    typed it, and a separator the user types in a comma-decimal field is
 *    decimal intent. Only that char converts.
 *  - Incremental edit that added a DIGIT → every "." present is the
 *    formatter's own grouping. Left alone; this is what keeps "1.234" + "9"
 *    mid-number at 19.234 and "1.234" + "5" at the end at 12.345.
 *  - Bulk change (paste / programmatic fill) → the string arrived complete, so
 *    the classic rule applies: a separator followed by exactly three digits is
 *    grouping ("1.234" → 1234), anything else is the decimal point ("3.5",
 *    "12.99", and the last "." of "1.234.5").
 *
 * Scoped to `group === "."` deliberately. The mirror case is NOT symmetric: in
 * an en-style locale a typed "," is unambiguously grouping, and reinterpreting
 * it would corrupt "1,234" into 1.234. Space-grouped locales are untouched.
 *
 * A raw that already contains the real decimal char is returned untouched —
 * the user has committed to "," and any "." is then genuinely grouping
 * ("1.000,50").
 */
export function resolveDecimalSeparatorIntent(
  raw: string,
  prev: string,
  group: string,
  decimal: string,
): string {
  if (group !== "." || decimal === ".") return raw;
  if (raw.includes(decimal)) return raw;
  const lastSep = raw.lastIndexOf(group);
  if (lastSep === -1) return raw;

  const countOf = (s: string) => s.split(group).length - 1;
  const incremental = Math.abs(raw.length - prev.length) === 1;

  if (incremental) {
    // Only a separator the user just ADDED is decimal intent.
    if (countOf(raw) <= countOf(prev)) return raw;
    let i = 0;
    while (i < prev.length && raw[i] === prev[i]) i++;
    if (raw[i] !== group) return raw;
    return raw.slice(0, i) + decimal + raw.slice(i + 1);
  }

  // Bulk: exactly three trailing digits reads as grouping, else decimal.
  const after = raw.slice(lastSep + 1);
  if (/^\d{3}$/.test(after)) return raw;
  return raw.slice(0, lastSep) + decimal + raw.slice(lastSep + 1);
}

/**
 * Format a raw decimal input string with live grouping of the INTEGER part
 * only. The fraction the user is mid-typing is left untouched (never padded /
 * trimmed here — that happens on blur), so trailing zeros and a bare trailing
 * decimal separator survive ("3," stays "3,"; "1000,50" → "1.000,50").
 */
export function formatDecimalGroupedLive(
  raw: string,
  locale: string,
  /** Previously displayed value — the discriminator for PB-TK-1. Omitting it
   *  means "no prior display", i.e. the bulk/paste reading. */
  prev = "",
): string {
  raw = String(raw ?? "");
  const { group, decimal } = localeSeparators(locale);
  const neg = raw.trim().startsWith("-");
  raw = resolveDecimalSeparatorIntent(raw, String(prev ?? ""), group, decimal);
  // Drop grouping separators; keep digits and decimal separators only.
  const cleaned = raw
    .split(group)
    .join("")
    .replace(new RegExp(`[^\\d${escapeRegExp(decimal)}]`, "g"), "");
  const firstDec = cleaned.indexOf(decimal);
  const sign = neg ? "-" : "";
  if (firstDec === -1) {
    return sign + groupDigits(cleaned, group);
  }
  const intPart = cleaned.slice(0, firstDec).replace(/\D/g, "");
  // Everything after the first decimal separator, with any further separators
  // stripped (a second "," is noise).
  const fracPart = cleaned.slice(firstDec + 1).replace(/\D/g, "");
  return sign + groupDigits(intPart, group) + decimal + fracPart;
}

/**
 * Strip grouping (and anything non-canonical) from an integer input string,
 * returning "sign + digits" — kept as a STRING so long integers keep full
 * precision (never via Number). "10.000" → "10000"; "-1.000" → "-1000".
 */
export function stripIntegerGrouping(raw: string, locale: string): string {
  raw = String(raw ?? "");
  const neg = raw.trim().startsWith("-");
  const digits = raw.replace(/[^\d]/g, "");
  return (neg ? "-" : "") + digits;
}

/**
 * Map a caret position from `oldStr` (what the browser produced after the raw
 * keystroke) to the reformatted `newStr`, by counting "significant" chars
 * (everything except the grouping separator) to the left of the caret and
 * placing the new caret after the same count. This keeps the cursor visually
 * anchored to the digit the user was editing even as separators shift.
 */
export function mapCaretAfterReformat(
  oldStr: string,
  oldCaret: number,
  newStr: string,
  locale: string,
): number {
  const { group } = localeSeparators(locale);
  let target = 0;
  for (let i = 0; i < oldCaret && i < oldStr.length; i++) {
    if (oldStr[i] !== group) target++;
  }
  if (target === 0) return 0;
  let count = 0;
  for (let i = 0; i < newStr.length; i++) {
    if (newStr[i] !== group) {
      count++;
      if (count === target) return i + 1;
    }
  }
  return newStr.length;
}

/**
 * Hook that turns an onChange value into a live-grouped draft while keeping the
 * caret anchored. Usage:
 *
 *   const inputRef = useRef<HTMLInputElement>(null);
 *   const format = useLiveGroupedInput({ inputRef, locale, mode: "decimal" });
 *   <FormInput ref={inputRef}
 *     onChangeAction={(v) => setDraft(format(v))} />
 *
 * The caller owns the draft state; the hook stores the desired caret and
 * restores it in a layout effect after React commits the reformatted value.
 */
export function useLiveGroupedInput({
  inputRef,
  locale,
  mode,
}: {
  inputRef: React.RefObject<HTMLInputElement | null>;
  locale: string;
  mode: "integer" | "decimal";
}): (raw: string) => string {
  const sep = localeSeparators(locale);
  const pendingCaret = useRef<number | null>(null);
  // The value currently SHOWN in the input, kept in sync with the DOM by the
  // layout effect below. Lets us tell, on the next keystroke, whether the
  // reformat produced a visible change (→ React re-renders) or not (→ React
  // bails on the equal value and we must reconcile the DOM ourselves).
  const shown = useRef<string>("");

  const reformat = (raw: string, prev: string): string =>
    mode === "integer"
      ? formatIntegerGroupedLive(raw, locale)
      : formatDecimalGroupedLive(raw, locale, prev);

  const format = (raw: string): string => {
    const el = inputRef.current;
    const oldCaret = el?.selectionStart ?? raw.length;
    const prev = shown.current;
    let formatted = reformat(raw, prev);
    let caretSrc = raw;
    let caretPos = oldCaret;

    // Deleting a grouping separator (e.g. Backspace at "1.|234") strips it from
    // `raw`, but grouping immediately re-inserts it → the reformat is a no-op
    // and the digit is never removed. Detect that (formatted unchanged, raw got
    // shorter) and delete the neighbouring digit so ONE keystroke deletes across
    // the separator, like a banking app.
    if (formatted === prev && raw.length < prev.length && oldCaret > 0) {
      const raw2 = raw.slice(0, oldCaret - 1) + raw.slice(oldCaret);
      formatted = reformat(raw2, prev);
      caretSrc = raw2;
      caretPos = oldCaret - 1;
    }

    // PB-TK-1: the caret mapper counts every char EXCEPT the group separator as
    // significant. When a typed "." has just been reinterpreted as the decimal
    // separator it is significant in `formatted` but still looks like a group
    // char in the raw — counting the raw as-is drops the caret BEFORE the new
    // comma, so the next keystroke lands on the wrong side ("3.5" → "35,").
    // Align the caret source with the same reinterpretation.
    const caretSrcAligned =
      mode === "decimal"
        ? resolveDecimalSeparatorIntent(caretSrc, prev, sep.group, sep.decimal)
        : caretSrc;

    pendingCaret.current = mapCaretAfterReformat(
      caretSrcAligned,
      caretPos,
      formatted,
      locale,
    );

    // When the reformatted string equals what was already shown, React skips
    // the re-render (Object.is-equal state), so the layout effect won't fire to
    // fix the DOM — which still holds the raw keystroke (a stripped char, or the
    // separator-delete no-op). Reconcile the input value + caret imperatively.
    if (el && formatted === prev && el.value !== formatted) {
      el.value = formatted;
      if (pendingCaret.current != null && document.activeElement === el) {
        el.setSelectionRange(pendingCaret.current, pendingCaret.current);
      }
      pendingCaret.current = null;
    }
    shown.current = formatted;
    return formatted;
  };

  useLayoutEffect(() => {
    const el = inputRef.current;
    if (el) {
      if (pendingCaret.current != null && document.activeElement === el) {
        const p = pendingCaret.current;
        el.setSelectionRange(p, p);
      }
      // Track whatever is actually in the input now (covers external resets that
      // bypass `format`, e.g. a parent `value` change re-seeding the draft).
      shown.current = el.value;
    }
    pendingCaret.current = null;
  });

  return format;
}
